import { memo, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import clsx from "clsx";
import makeStyles from "@mui/styles/makeStyles";
import CreateIcon from "@mui/icons-material/Create";

import { NON_DELIVERY_PLAN_TYPES } from "../../consts/planTypes";
import { ROUTE_STATUS } from "../../consts/routeDetails";
import { palette } from "../../theme/palette";

import {
  getMatrixProfileType,
  routeInEditSelector,
  volumeDataSelector,
  wavesSelector,
} from "../../reducers/matrixProfile/selectors";
import {
  selectedTimeBandSelector,
  selectedCollectionTabSelector,
} from "../../reducers/ownApp/selectors";

import {
  CheckmarkIcon,
  ClockIcon,
  WarningIcon,
  OtherVolumesIcon,
  BinIcon,
} from "../Icons";
import { NoWaveSelected } from "../Modals/NoWaveSelected/NoWaveSelected";
import VolumePopup from "./VolumePopup";
import RouteLabel from "./RouteLabel";
import DriverLabel from "./DriverLabel";
import { View } from "../View";

const useStyles = makeStyles(() => ({
  section: {
    display: "flex",
    flexDirection: "row",
    background: palette.typography.white.neutral,
    padding: "0.625rem",
    paddingBottom: "0.5rem",
    cursor: "default",
  },
  selected: {
    background: palette.typography.white.disabled,
  },
  editIconBase: {
    backgroundColor: (props) =>
      props.editing
        ? palette.orange
        : props.notInWave
        ? "#30303044"
        : "#303030",
    borderRadius: "50%",
    width: "24px",
    height: "24px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  editIcon: {
    color: palette.typography.white.solid,
    width: "13px",
    height: "13px",
    cursor: "pointer",
  },
  routeIconsContainer: {
    display: "flex",
    flexDirection: "column",
  },
  appIcon: {
    marginTop: "10px",
    fill: palette.typography.white.solid,
    height: "20px",
    width: "20px",
  },
  binIcon: {
    marginTop: "10px",
    fill: palette.typography.white.solid,
    height: "20px",
    width: "20px",
    cursor: "pointer",
  },
  popupIcon: {
    marginTop: "10px",
    fill: palette.typography.white.medium,
    height: "18px",
    width: "24px",
    cursor: "pointer",
  },
  left: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
  },
  right: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    marginLeft: "0.313rem",
  },
}));

const BaseCard = ({
  children,
  selected,
  route,
  driver,
  onClick,
  onEdit,
  onClear,
  scrollOffset,
  onEditDriverClick,
}) => {
  const [noWaveWarning, setNoWaveWarning] = useState();
  const [otherVolumeAnchorEl, setOtherVolumeAnchorEl] = useState(null);
  const [notInWave, setNotInWave] = useState();
  const routeInEdit = useSelector(routeInEditSelector);
  const volumeData = useSelector(volumeDataSelector);
  const waves = useSelector(wavesSelector);
  const selectedTimeBand = useSelector(selectedTimeBandSelector);
  const planType = useSelector(getMatrixProfileType);
  const collectionType = useSelector(selectedCollectionTabSelector);
  const styles = useStyles({
    editing: routeInEdit?.routeCode === route.routeCode,
    notInWave,
  });

  const handleClick = (e) => {
    e.preventDefault();
    if (otherVolumeAnchorEl) setOtherVolumeAnchorEl(null);
    else onClick && onClick();
  };

  useEffect(() => {
    const currentWave = waves.find(
      (wave) => wave.waveTime === selectedTimeBand
    );
    setNotInWave(selectedTimeBand !== "default" && !currentWave);
  }, [waves, selectedTimeBand]);

  useEffect(() => {
    setOtherVolumeAnchorEl(null);
  }, [scrollOffset]);

  const handleEdit = (e) => {
    e.stopPropagation();
    if (!NON_DELIVERY_PLAN_TYPES.includes(planType) && notInWave)
      setNoWaveWarning(true);
    else if (onEdit) onEdit();
  };

  const handleClear = (e) => {
    e.stopPropagation();
    if (!NON_DELIVERY_PLAN_TYPES.includes(planType) && notInWave)
      setNoWaveWarning(true);
    else if (onClear) onClear();
  };

  return (
    <View>
      <View
        className={clsx(styles.section, selected && styles.selected)}
        onClick={handleClick}
      >
        <View className={styles.left}>
          <RouteLabel
            route={route}
            planType={planType}
            collectionType={collectionType}
            onClick={onEditDriverClick}
          />
          <View className={styles.editIconBase} onClick={handleEdit}>
            <CreateIcon fontSize={"small"} className={styles.editIcon} />
          </View>
          <BinIcon className={styles.binIcon} onClick={handleClear} />

          {volumeData && (
            <View className={styles.routeIconsContainer}>
              {!!route?.stops?.length && (
                <>
                  <OtherVolumesIcon
                    className={styles.popupIcon}
                    onClick={(event) => {
                      event.stopPropagation();
                      if (otherVolumeAnchorEl) setOtherVolumeAnchorEl(null);
                      else setOtherVolumeAnchorEl(event.currentTarget);
                    }}
                  />
                  {otherVolumeAnchorEl && (
                    <VolumePopup
                      route={route}
                      type={planType}
                      open={true}
                      anchorEl={otherVolumeAnchorEl}
                    />
                  )}
                </>
              )}

              {route?.routeOptimisation?.routeStatus ===
                ROUTE_STATUS.DUTY_EXCEEDED && (
                <WarningIcon className={styles.appIcon} />
              )}
              {route?.routeOptimisation?.routeStatus ===
                ROUTE_STATUS.OPPORTUNITY && (
                <ClockIcon className={styles.appIcon} />
              )}
              {route?.routeOptimisation?.routeStatus === ROUTE_STATUS.OK && (
                <CheckmarkIcon className={styles.appIcon} />
              )}
            </View>
          )}
        </View>
        <View className={styles.right}>
          <DriverLabel driver={driver} route={route} />
          {children}
        </View>
      </View>

      <NoWaveSelected
        open={noWaveWarning}
        onClose={() => {
          setNoWaveWarning(false);
        }}
        onContinue={() => {
          setNoWaveWarning(false);
        }}
      />
    </View>
  );
};

export default memo(BaseCard);
